import { Box, Radio, RadioGroup, Stack, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { Warning } from '@phosphor-icons/react';
import { ChangeEvent, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'core/elements';
import { useFormContext } from 'core/states';
import { renderHtmlMessage } from 'core/utils';
import { PriceBasedOn } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { ICustomerQuotePart } from 'quotes/types';
import { Color, Level } from 'theme/enums';

interface IProps {
  selectedQuoteParts: ICustomerQuotePart[];
  title?: ReactNode;
  sx?: SxProps;
}

export const PriceBasedOnBulkField = ({ selectedQuoteParts, title, sx = {} }: IProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext();

  const priceBasedOnValue = watch('priceBasedOn') || PriceBasedOn.QUOTED;

  const bulkReplacementDisabled = useMemo(
    () => !selectedQuoteParts.some(QuotePartHelper.isEligibleForReplacementPricing),
    [selectedQuoteParts]
  );

  const ineligibleLineLabels = useMemo(() => {
    const isIneligible = (quotePart: ICustomerQuotePart) =>
      priceBasedOnValue === PriceBasedOn.REPLACEMENT
        ? !QuotePartHelper.isEligibleForReplacementPricing(quotePart)
        : QuotePartHelper.isPriceLocked(quotePart);

    return selectedQuoteParts
      .filter(isIneligible)
      .map((quotePart) => [quotePart.line, quotePart.secondaryLine].filter(Boolean).join('.'));
  }, [priceBasedOnValue, selectedQuoteParts]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setValue('priceBasedOn', event.target.value);
    },
    [setValue]
  );

  const replacementRadio = (
    <Radio
      value={PriceBasedOn.REPLACEMENT}
      label={t('labelUseReplacementCostToCalculatePrice')}
      disabled={bulkReplacementDisabled}
    />
  );

  return (
    <Stack spacing={1} sx={sx}>
      {title && <Typography level={Level.TITLE_MD}>{title}</Typography>}

      <RadioGroup value={priceBasedOnValue} onChange={onChange}>
        <Radio value={PriceBasedOn.QUOTED} label={t('labelUseQuotedCostToCalculatePrice')} />

        {bulkReplacementDisabled ? (
          <Tooltip
            title={renderHtmlMessage(t('messageBulkNoReplacementCostAvailable'))}
            color={Color.WARNING}
          >
            <Box sx={{ marginBlockStart: 'var(--RadioGroup-gap)' }}>{replacementRadio}</Box>
          </Tooltip>
        ) : (
          replacementRadio
        )}
      </RadioGroup>

      {ineligibleLineLabels.length > 0 && (
        <Typography
          color={Color.WARNING}
          level={Level.TITLE_SM}
          startDecorator={<Warning weight="fill" />}
        >
          {renderHtmlMessage(
            t(
              priceBasedOnValue === PriceBasedOn.REPLACEMENT
                ? 'messageBulkPriceBasedOnIneligibleLinesReplacement'
                : 'messageBulkPriceBasedOnIneligibleLinesQuoted',
              {
                count: ineligibleLineLabels.length,
                total: selectedQuoteParts.length,
                lines: ineligibleLineLabels.join(', '),
              }
            )
          )}
        </Typography>
      )}
    </Stack>
  );
};
